import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { RotateCcw, Check, X, AlertTriangle } from "lucide-react";

// Current market rate benchmark (30-yr fixed, US average, updated Feb 2026).
// Bump this when Fed/market conditions shift meaningfully (>50 bps).
const CURRENT_MARKET_RATE = 6.5;
const STALE_RATE_THRESHOLD = 5.5;

export default function AnalysisDefaultsEditor({ defaults, onSave, onReset, onCancel }) {
  const [form, setForm] = useState({
    down_payment_percent: defaults.down_payment_percent ?? 20,
    closing_costs_percent: defaults.closing_costs_percent ?? 3,
    repair_costs: defaults.repair_costs ?? 5000,
    loan_term_years: defaults.loan_term_years ?? 30,
    interest_rate: defaults.interest_rate ?? 6.5,
    capex_percent: defaults.capex_percent ?? 0,
    loan_points: defaults.loan_points ?? 0,
    electricity: defaults.electricity ?? 0,
    gas: defaults.gas ?? 0,
    water_sewer: defaults.water_sewer ?? 0,
    garbage: defaults.garbage ?? 0,
    other_expenses: defaults.other_expenses ?? 0,
    // Flip defaults
    flip_rehab_scope: defaults.flip_rehab_scope ?? "medium",
    flip_rehab_per_sqft: defaults.flip_rehab_per_sqft ?? 0,
    flip_holding_months: defaults.flip_holding_months ?? 5,
    flip_financing_rate: defaults.flip_financing_rate ?? 12.0,
    flip_financing_points: defaults.flip_financing_points ?? 2.0,
    flip_financing_ltv: defaults.flip_financing_ltv ?? 85.0,
    flip_agent_commission: defaults.flip_agent_commission ?? 5.0,
    flip_contingency_pct: defaults.flip_contingency_pct ?? 10.0,
    flip_monthly_utilities: defaults.flip_monthly_utilities ?? 200,
  });

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const Field = ({ label, field, min = 0, max, step, prefix }) => (
    <div>
      <label className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground mt-0.5">{prefix}</span>}
        <Input
          type="number"
          value={form[field]}
          onChange={(e) => handleChange(field, parseFloat(e.target.value) || 0)}
          className={`font-mono mt-1 h-8 text-sm ${prefix ? 'pl-5' : ''}`}
          min={min} max={max} step={step}
          data-testid={`default-${field.replace(/_/g, '-')}`}
        />
      </div>
    </div>
  );

  return (
    <div data-testid="analysis-defaults-editor">
      {/* Stale rate warning — nudge users still holding 3–4% pandemic-era defaults */}
      {form.interest_rate > 0 && form.interest_rate < STALE_RATE_THRESHOLD && (
        <div
          className="mb-3 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5"
          data-testid="stale-rate-warning"
        >
          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-amber-300 font-medium">
              Your interest rate ({form.interest_rate}%) is well below the current market (~{CURRENT_MARKET_RATE}%).
            </p>
            <p className="text-[11px] text-amber-200/80 mt-0.5">
              Every cash-flow number in the app will look rosier than reality until you refresh this.
            </p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleChange("interest_rate", CURRENT_MARKET_RATE)}
            className="text-[11px] h-7 px-2 text-amber-300 hover:text-amber-100 hover:bg-amber-500/20 flex-shrink-0"
            data-testid="use-market-rate-btn"
          >
            Use {CURRENT_MARKET_RATE}%
          </Button>
        </div>
      )}

      {/* Financing */}
      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-2">Financing</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
        <Field label="Down Payment %" field="down_payment_percent" max={100} />
        <Field label="Interest Rate %" field="interest_rate" max={20} step={0.1} />
        <Field label="Loan Term (Yrs)" field="loan_term_years" max={40} />
        <Field label="Closing Costs %" field="closing_costs_percent" max={10} />
        <Field label="Loan Points" field="loan_points" max={10} step={0.25} />
      </div>

      {/* Property */}
      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-2">Property Expenses</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
        <Field label="Repair Costs" field="repair_costs" prefix="$" />
        <Field label="CapEx (% income)" field="capex_percent" max={50} />
        <Field label="Electricity /mo" field="electricity" prefix="$" />
        <Field label="Gas /mo" field="gas" prefix="$" />
        <Field label="Water & Sewer /mo" field="water_sewer" prefix="$" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
        <Field label="Garbage /mo" field="garbage" prefix="$" />
        <Field label="Other /mo" field="other_expenses" prefix="$" />
      </div>

      {/* Flip Defaults */}
      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-2">Flip / Hard Money</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
        <div>
          <label className="text-[10px] text-muted-foreground uppercase tracking-wide">Rehab Scope</label>
          <select
            value={form.flip_rehab_scope}
            onChange={(e) => handleChange("flip_rehab_scope", e.target.value)}
            className="w-full font-mono mt-1 h-8 text-sm rounded-md border border-input bg-background px-2"
            data-testid="default-flip-rehab-scope"
          >
            <option value="cosmetic">Cosmetic ($15/sqft)</option>
            <option value="medium">Medium ($30/sqft)</option>
            <option value="gut">Gut ($55/sqft)</option>
          </select>
        </div>
        <Field label="HM Rate %" field="flip_financing_rate" max={25} step={0.5} />
        <Field label="HM Points %" field="flip_financing_points" max={10} step={0.5} />
        <Field label="LTV %" field="flip_financing_ltv" max={100} />
        <Field label="Hold (months)" field="flip_holding_months" max={24} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
        <Field label="Commission %" field="flip_agent_commission" max={10} step={0.5} />
        <Field label="Contingency %" field="flip_contingency_pct" max={50} />
        <Field label="Utilities $/mo" field="flip_monthly_utilities" prefix="$" />
      </div>

      <div className="flex items-center justify-end gap-2">
        {onReset && (
          <Button variant="ghost" size="sm" onClick={onReset} className="text-xs h-7 text-muted-foreground" data-testid="reset-defaults-btn">
            <RotateCcw className="w-3 h-3 mr-1" /> Reset to Platform Defaults
          </Button>
        )}
        {onCancel && (
          <Button variant="ghost" size="sm" onClick={onCancel} className="text-xs h-7" data-testid="cancel-defaults-btn">
            <X className="w-3 h-3 mr-1" /> Cancel
          </Button>
        )}
        <Button size="sm" onClick={() => onSave(form)} className="text-xs h-7 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-defaults-btn">
          <Check className="w-3 h-3 mr-1" /> Save Defaults
        </Button>
      </div>
    </div>
  );
}
